// packages/character-render/src/proceduralHumanoid.ts
//
// Procedural humanoid builder: role-based palettes and silhouettes built from
// a few scaled boxes per limb (legs side by side, two-tone boots/trousers,
// sleeves with hands, a face, hats and costume pieces). Output is plain
// `HumanoidPart[]`, so it renders through <HumanoidMesh> in 1–3 draw calls.
//
// Deterministic: the same (role, seed) always yields the same figure.
import * as THREE from 'three';
import type { HumanoidBone, HumanoidPart } from './humanoidRig';

export type HatStyle =
  | 'none'
  | 'tricorn'
  | 'bandana'
  | 'turban'
  | 'helmet'
  | 'straw'
  | 'cap'
  | 'conical';
export type CoatStyle = 'none' | 'short' | 'long' | 'robe';
export type BuildStyle = 'slim' | 'regular' | 'stout';

export interface HumanoidPalette {
  skin: string;
  hair: string;
  shirt: string;
  /** Second shirt colour; with `stripes` the torso alternates the two. */
  shirtAlt?: string;
  coat?: string;
  pants: string;
  boots: string;
  belt: string;
  hat?: string;
  /** Trim / metal / sash colour. */
  accent: string;
}

export interface ProceduralHumanoidSpec {
  /** Standing height in world units. Default 1.9. */
  height?: number;
  build?: BuildStyle;
  palette: HumanoidPalette;
  hat?: HatStyle;
  coat?: CoatStyle;
  stripes?: boolean;
  beard?: boolean;
  epaulettes?: boolean;
  apron?: boolean;
  sash?: boolean;
  cutlass?: boolean;
  /** Hair falls down the back to the shoulders. */
  longHair?: boolean;
}

type V3 = [number, number, number];

const EYE = '#1b1616';
const METAL = { metalness: 0.7, roughness: 0.35 } as const;

function box(
  name: HumanoidPart['name'],
  color: string,
  size: V3,
  center: V3,
  style: {
    bone?: HumanoidBone;
    roughness?: number;
    metalness?: number;
    rotation?: V3;
    emissive?: string;
  } = {},
): HumanoidPart {
  const { rotation, ...surface } = style;
  return {
    name,
    voxels: [{ x: 0, y: 0, z: 0, color }],
    scale: size,
    offset: center,
    ...(rotation ? { rotation } : {}),
    ...surface,
  };
}

/** Vertical stack of equal boxes, bottom→top colours, bottom face at `baseY`. */
function stack(
  name: HumanoidPart['name'],
  colors: readonly string[],
  size: V3,
  base: V3,
  style: { bone?: HumanoidBone; roughness?: number } = {},
): HumanoidPart {
  return {
    name,
    voxels: colors.map((color, i) => ({ x: 0, y: i, z: 0, color })),
    scale: size,
    offset: [base[0], base[1] + size[1] / 2, base[2]],
    ...style,
  };
}

/**
 * Build a humanoid from a spec. Proportions are authored for a 1-unit figure
 * and scaled to `height`. The figure faces +Z with its feet at y = 0.
 */
export function buildProceduralHumanoid(spec: ProceduralHumanoidSpec): HumanoidPart[] {
  const H = spec.height ?? 1.9;
  const p = spec.palette;
  const build = spec.build ?? 'regular';
  const torsoW = build === 'slim' ? 0.3 : build === 'stout' ? 0.38 : 0.34;
  const torsoD = build === 'stout' ? 0.22 : 0.19;
  const legW = build === 'stout' ? 0.13 : 0.12;
  const legX = build === 'stout' ? 0.078 : 0.068;
  const sleeve = spec.coat && spec.coat !== 'none' ? (p.coat ?? p.shirt) : p.shirt;
  const upper = spec.coat && spec.coat !== 'none' ? (p.coat ?? p.shirt) : p.shirt;
  const alt = spec.stripes ? (p.shirtAlt ?? p.shirt) : upper;

  const parts: HumanoidPart[] = [];
  const sv = (v: V3): V3 => [v[0] * H, v[1] * H, v[2] * H];

  // Legs — side by side, boots then trousers.
  for (const side of [-1, 1] as const) {
    parts.push(
      stack(
        side < 0 ? 'left_leg' : 'right_leg',
        [p.boots, p.pants, p.pants, p.pants],
        sv([legW, 0.11, 0.14]),
        sv([side * legX, 0, 0]),
        {
          roughness: 0.88,
        },
      ),
    );
  }

  // Torso — belt at the waist, then shirt/coat (optionally striped).
  parts.push(
    stack('torso', [p.belt, upper, alt, upper], sv([torsoW, 0.08, torsoD]), sv([0, 0.44, 0]), {
      roughness: 0.85,
    }),
  );

  // Open coat shows the shirt down the front.
  if (spec.coat && spec.coat !== 'none') {
    parts.push(
      box('misc', p.shirt, sv([0.11, 0.25, 0.02]), sv([0, 0.63, torsoD / 2 + 0.006]), {
        bone: 'body',
      }),
    );
    if (spec.coat === 'long' || spec.coat === 'robe') {
      const skirtH = spec.coat === 'robe' ? 0.36 : 0.2;
      parts.push(
        box(
          'misc',
          p.coat ?? p.shirt,
          sv([torsoW + 0.03, skirtH, torsoD + 0.03]),
          sv([0, 0.46 - skirtH / 2, 0]),
          {
            bone: 'body',
            roughness: 0.9,
          },
        ),
      );
    }
  }

  if (build === 'stout') {
    parts.push(
      box('misc', upper, sv([torsoW - 0.05, 0.15, 0.06]), sv([0, 0.55, torsoD / 2 + 0.02]), {
        bone: 'body',
      }),
    );
  }
  if (spec.apron) {
    parts.push(
      box(
        'misc',
        p.accent,
        sv([0.25, 0.3, 0.02]),
        sv([0, 0.55, torsoD / 2 + (build === 'stout' ? 0.055 : 0.012)]),
        { bone: 'body', roughness: 0.95 },
      ),
    );
  }
  if (spec.sash) {
    parts.push(
      box('misc', p.accent, sv([torsoW + 0.07, 0.045, torsoD + 0.025]), sv([0, 0.62, 0]), {
        bone: 'body',
        rotation: [0, 0, 0.62],
      }),
    );
  }
  if (spec.epaulettes) {
    for (const side of [-1, 1] as const) {
      parts.push(
        box(
          'misc',
          p.accent,
          sv([0.12, 0.035, 0.14]),
          sv([side * (torsoW / 2 + 0.035), 0.775, 0]),
          { bone: 'body', ...METAL },
        ),
      );
    }
  }
  if (spec.cutlass) {
    parts.push(
      box('misc', '#cfd4da', sv([0.025, 0.34, 0.05]), sv([-(torsoW / 2 + 0.03), 0.33, 0.05]), {
        bone: 'body',
        ...METAL,
      }),
    );
    parts.push(
      box('misc', p.accent, sv([0.07, 0.03, 0.07]), sv([-(torsoW / 2 + 0.03), 0.515, 0.05]), {
        bone: 'body',
        ...METAL,
      }),
    );
  }

  // Arms — hand, then two sleeve segments, hanging from the shoulder.
  const armX = torsoW / 2 + 0.056;
  for (const side of [-1, 1] as const) {
    parts.push(
      stack(
        side < 0 ? 'left_arm' : 'right_arm',
        [p.skin, sleeve, sleeve],
        sv([0.1, 0.107, 0.11]),
        sv([side * armX, 0.445, 0]),
        {
          roughness: 0.8,
        },
      ),
    );
  }

  // Head + face.
  parts.push(box('head', p.skin, sv([0.24, 0.235, 0.225]), sv([0, 0.8875, 0]), { roughness: 0.6 }));
  parts.push(box('head', p.skin, sv([0.1, 0.03, 0.1]), sv([0, 0.77, 0]), { roughness: 0.6 })); // neck
  for (const side of [-1, 1] as const) {
    parts.push(
      box('misc', EYE, sv([0.04, 0.035, 0.02]), sv([side * 0.055, 0.905, 0.114]), {
        bone: 'head',
        roughness: 0.3,
      }),
    );
  }
  parts.push(
    box('misc', p.skin, sv([0.035, 0.05, 0.035]), sv([0, 0.865, 0.122]), {
      bone: 'head',
      roughness: 0.6,
    }),
  ); // nose

  // Hair (under any hat) and beard.
  parts.push(
    box('misc', p.hair, sv([0.25, 0.05, 0.235]), sv([0, 1.0, -0.004]), {
      bone: 'head',
      roughness: 0.9,
    }),
  );
  parts.push(
    box('misc', p.hair, sv([0.25, 0.15, 0.04]), sv([0, 0.925, -0.112]), {
      bone: 'head',
      roughness: 0.9,
    }),
  );
  if (spec.longHair) {
    parts.push(
      box('misc', p.hair, sv([0.25, 0.22, 0.05]), sv([0, 0.8, -0.13]), {
        bone: 'head',
        roughness: 0.9,
      }),
    );
  }
  if (spec.beard) {
    parts.push(
      box('misc', p.hair, sv([0.2, 0.085, 0.045]), sv([0, 0.8, 0.108]), {
        bone: 'head',
        roughness: 0.95,
      }),
    );
  }

  const hat = p.hat ?? p.accent;
  switch (spec.hat ?? 'none') {
    case 'tricorn':
      parts.push(
        box('misc', hat, sv([0.37, 0.035, 0.32]), sv([0, 1.012, 0]), {
          bone: 'head',
          roughness: 0.8,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.262, 0.022, 0.242]), sv([0, 1.04, 0]), {
          bone: 'head',
          ...METAL,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.25, 0.085, 0.23]), sv([0, 1.07, 0]), {
          bone: 'head',
          roughness: 0.8,
        }),
      );
      break;
    case 'bandana':
      parts.push(
        box('misc', hat, sv([0.258, 0.075, 0.245]), sv([0, 0.99, 0]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.06, 0.07, 0.05]), sv([0.04, 0.95, -0.135]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      break;
    case 'turban':
      parts.push(
        box('misc', hat, sv([0.29, 0.13, 0.28]), sv([0, 1.03, 0]), {
          bone: 'head',
          roughness: 0.92,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.045, 0.045, 0.02]), sv([0, 1.03, 0.145]), {
          bone: 'head',
          ...METAL,
        }),
      );
      break;
    case 'helmet':
      parts.push(
        box('misc', '#9aa2ab', sv([0.275, 0.1, 0.265]), sv([0, 1.0, 0]), {
          bone: 'head',
          ...METAL,
        }),
      );
      parts.push(
        box('misc', '#9aa2ab', sv([0.33, 0.02, 0.31]), sv([0, 0.955, 0]), {
          bone: 'head',
          ...METAL,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.04, 0.07, 0.25]), sv([0, 1.075, 0]), {
          bone: 'head',
          roughness: 0.7,
        }),
      );
      break;
    case 'straw':
      parts.push(
        box('misc', hat, sv([0.44, 0.025, 0.44]), sv([0, 1.005, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.22, 0.08, 0.22]), sv([0, 1.055, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      parts.push(
        box('misc', p.accent, sv([0.225, 0.02, 0.225]), sv([0, 1.025, 0]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      break;
    case 'conical':
      parts.push(
        box('misc', hat, sv([0.46, 0.03, 0.46]), sv([0, 1.0, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.3, 0.05, 0.3]), sv([0, 1.04, 0]), { bone: 'head', roughness: 0.95 }),
      );
      parts.push(
        box('misc', hat, sv([0.16, 0.05, 0.16]), sv([0, 1.09, 0]), {
          bone: 'head',
          roughness: 0.95,
        }),
      );
      break;
    case 'cap':
      parts.push(
        box('misc', hat, sv([0.26, 0.065, 0.25]), sv([0, 1.0, 0]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      parts.push(
        box('misc', hat, sv([0.2, 0.02, 0.09]), sv([0, 0.975, 0.155]), {
          bone: 'head',
          roughness: 0.9,
        }),
      );
      break;
    default:
      break;
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export type HumanoidRole =
  | 'merchant'
  | 'sailor'
  | 'officer'
  | 'harbormaster'
  | 'guard'
  | 'guide'
  | 'noble'
  | 'villager';

export const HUMANOID_ROLES: readonly HumanoidRole[] = [
  'merchant',
  'sailor',
  'officer',
  'harbormaster',
  'guard',
  'guide',
  'noble',
  'villager',
];

const SKIN = ['#f1c9a5', '#e2b088', '#c98e62', '#a86d45', '#7e4d2f', '#5c3822'];
const HAIR = ['#1f1a17', '#3b2717', '#5e3b20', '#94652f', '#7d7a76'];

interface RoleTemplate {
  build: BuildStyle[];
  height: number;
  hat: HatStyle[];
  coat: CoatStyle;
  beardChance: number;
  stripes?: boolean;
  epaulettes?: boolean;
  apron?: boolean;
  sash?: boolean;
  cutlass?: boolean;
  clothes: Array<Omit<HumanoidPalette, 'skin' | 'hair'>>;
}

const ROLE_TEMPLATES: Record<HumanoidRole, RoleTemplate> = {
  merchant: {
    build: ['stout', 'regular'],
    height: 1.82,
    hat: ['turban', 'cap'],
    coat: 'none',
    beardChance: 0.6,
    apron: true,
    clothes: [
      {
        shirt: '#a8452f',
        pants: '#4a3526',
        boots: '#2e211a',
        belt: '#1f1611',
        hat: '#e9dfc8',
        accent: '#e6d8b8',
      },
      {
        shirt: '#6f4a8a',
        pants: '#3d3228',
        boots: '#2b201a',
        belt: '#1c1510',
        hat: '#d8b04a',
        accent: '#efe3c6',
      },
      {
        shirt: '#b5822f',
        pants: '#4b3b2b',
        boots: '#2e221b',
        belt: '#23190f',
        hat: '#f1ead8',
        accent: '#dccfae',
      },
    ],
  },
  sailor: {
    build: ['slim', 'regular'],
    height: 1.8,
    hat: ['bandana', 'bandana', 'cap'],
    coat: 'none',
    beardChance: 0.4,
    stripes: true,
    clothes: [
      {
        shirt: '#eeeae0',
        shirtAlt: '#23365e',
        pants: '#c7b58a',
        boots: '#3a2a1e',
        belt: '#5a3b20',
        hat: '#b3261e',
        accent: '#b3261e',
      },
      {
        shirt: '#eeeae0',
        shirtAlt: '#8e1f1f',
        pants: '#3e4f6b',
        boots: '#2d2119',
        belt: '#4a3019',
        hat: '#2c4a7a',
        accent: '#2c4a7a',
      },
    ],
  },
  officer: {
    build: ['regular', 'slim'],
    height: 1.96,
    hat: ['tricorn'],
    coat: 'long',
    beardChance: 0.15,
    epaulettes: true,
    clothes: [
      {
        shirt: '#f2efe6',
        coat: '#1f2d57',
        pants: '#ece6d6',
        boots: '#141414',
        belt: '#141414',
        hat: '#15151a',
        accent: '#d4a93c',
      },
      {
        shirt: '#f2efe6',
        coat: '#7d1c1c',
        pants: '#ece6d6',
        boots: '#141414',
        belt: '#141414',
        hat: '#15151a',
        accent: '#d4a93c',
      },
    ],
  },
  harbormaster: {
    build: ['stout', 'regular'],
    height: 1.9,
    hat: ['tricorn'],
    coat: 'long',
    beardChance: 0.85,
    clothes: [
      {
        shirt: '#ece0c4',
        coat: '#5b3a22',
        pants: '#2f2a26',
        boots: '#1d1712',
        belt: '#1a120c',
        hat: '#2a1d14',
        accent: '#c9a24a',
      },
      {
        shirt: '#e8dcc0',
        coat: '#2f4a4a',
        pants: '#2c2824',
        boots: '#1d1712',
        belt: '#1a120c',
        hat: '#1f1f1f',
        accent: '#c9a24a',
      },
    ],
  },
  guard: {
    build: ['regular', 'stout'],
    height: 1.94,
    hat: ['helmet'],
    coat: 'short',
    beardChance: 0.5,
    cutlass: true,
    clothes: [
      {
        shirt: '#d9d2c2',
        coat: '#7a1f1f',
        pants: '#5a5550',
        boots: '#1f1a16',
        belt: '#2a1d12',
        accent: '#c9a24a',
      },
      {
        shirt: '#d9d2c2',
        coat: '#2c3f6b',
        pants: '#55524e',
        boots: '#1f1a16',
        belt: '#2a1d12',
        accent: '#e0e0e0',
      },
    ],
  },
  guide: {
    build: ['slim'],
    height: 1.78,
    hat: ['straw', 'none'],
    coat: 'none',
    beardChance: 0.25,
    sash: true,
    clothes: [
      {
        shirt: '#e6dbbd',
        pants: '#8a6a45',
        boots: '#4a3322',
        belt: '#3a2716',
        hat: '#d9bf73',
        accent: '#2f7d6d',
      },
      {
        shirt: '#d7e0d4',
        pants: '#6d5a44',
        boots: '#3f2c1f',
        belt: '#3a2716',
        hat: '#cfb56a',
        accent: '#b8492a',
      },
    ],
  },
  noble: {
    build: ['regular', 'stout'],
    height: 1.88,
    hat: ['turban', 'tricorn'],
    coat: 'robe',
    beardChance: 0.7,
    sash: true,
    clothes: [
      {
        shirt: '#f3ead2',
        coat: '#4a2767',
        pants: '#2a2230',
        boots: '#1a1418',
        belt: '#c9a24a',
        hat: '#f1e6c8',
        accent: '#d4a93c',
      },
      {
        shirt: '#f3ead2',
        coat: '#16594f',
        pants: '#1f2a28',
        boots: '#15120f',
        belt: '#c9a24a',
        hat: '#e9d9b0',
        accent: '#d4a93c',
      },
    ],
  },
  villager: {
    build: ['regular', 'slim', 'stout'],
    height: 1.8,
    hat: ['none', 'cap', 'straw'],
    coat: 'none',
    beardChance: 0.35,
    clothes: [
      {
        shirt: '#b99a6b',
        pants: '#5a4633',
        boots: '#33251a',
        belt: '#2a1d12',
        hat: '#8a6a45',
        accent: '#e8dcc0',
      },
      {
        shirt: '#7c8f5a',
        pants: '#4d4032',
        boots: '#2e2218',
        belt: '#2a1d12',
        hat: '#d9bf73',
        accent: '#e8dcc0',
      },
    ],
  },
};

/** FNV-1a 32-bit hash of a string — stable seed from an NPC id. */
export function hashSeed(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function jitter(hex: string, rnd: () => number, amount = 0.035): string {
  const _c = new THREE.Color();
  _c.set(hex);
  _c.offsetHSL((rnd() - 0.5) * 0.02, (rnd() - 0.5) * amount, (rnd() - 0.5) * amount);
  return `#${_c.getHexString()}`;
}

function pick<T>(list: readonly T[], rnd: () => number): T {
  return list[Math.floor(rnd() * list.length) % list.length]!;
}

/** Deterministic spec for a role; `seedKey` (e.g. the NPC id) picks the variant. */
export function humanoidSpecForRole(role: HumanoidRole, seedKey: string): ProceduralHumanoidSpec {
  const tpl = ROLE_TEMPLATES[role];
  const rnd = mulberry32(hashSeed(`${role}:${seedKey}`));
  const clothes = pick(tpl.clothes, rnd);
  const palette: HumanoidPalette = {
    skin: pick(SKIN, rnd),
    hair: pick(HAIR, rnd),
    shirt: jitter(clothes.shirt, rnd),
    ...(clothes.shirtAlt ? { shirtAlt: jitter(clothes.shirtAlt, rnd) } : {}),
    ...(clothes.coat ? { coat: jitter(clothes.coat, rnd) } : {}),
    pants: jitter(clothes.pants, rnd),
    boots: clothes.boots,
    belt: clothes.belt,
    ...(clothes.hat ? { hat: jitter(clothes.hat, rnd) } : {}),
    accent: clothes.accent,
  };
  return {
    height: tpl.height * (0.97 + rnd() * 0.06),
    build: pick(tpl.build, rnd),
    palette,
    hat: pick(tpl.hat, rnd),
    coat: tpl.coat,
    stripes: tpl.stripes ?? false,
    beard: rnd() < tpl.beardChance,
    epaulettes: tpl.epaulettes ?? false,
    apron: tpl.apron ?? false,
    sash: tpl.sash ?? false,
    cutlass: tpl.cutlass ?? false,
  };
}

/** Parts for a role-styled humanoid; stable for a given `(role, seedKey)`. */
export function humanoidPartsForRole(
  role: HumanoidRole,
  seedKey: string,
  height?: number,
): HumanoidPart[] {
  const spec = humanoidSpecForRole(role, seedKey);
  return buildProceduralHumanoid(height ? { ...spec, height } : spec);
}
